import React from 'react';
import { Link as RouterLink, Navigate, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import NotFoundPage from '../Pages/NotFoundPage';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAdmin?: boolean;
    /**
     * Klíč feature flagu (MODUL_* / FEATURE_*) — při vypnutém flagu se renderuje 404
     * (skryté funkce se nemapují; jen viditelnost D1, bezpečnost drží role na BE D2).
     */
    requireFeature?: string;
}

/**
 * Vnitřní guard jako samostatná komponenta — useFeatureFlag (a tím authenticated
 * app-config query) se volá JEN u rout s requireFeature a až PO auth kontrolách.
 */
const FeatureGuard: React.FC<{ flag: string; children: React.ReactNode }> = ({ flag, children }) => {
    const { enabled, isLoading } = useFeatureFlag(flag);

    if (isLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Načítám konfiguraci...
                </Typography>
            </Box>
        );
    }

    // Fail-closed: vypnutý/neznámý flag i chyba před prvním načtením => 404.
    if (!enabled) {
        return <NotFoundPage />;
    }

    return <>{children}</>;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requireAdmin = false,
    requireFeature,
}) => {
    const { isAuthenticated, isAdmin, isLoading } = useAuth();
    const location = useLocation();

    // Show loading while checking auth status
    if (isLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Ověřuji přihlášení...
                </Typography>
            </Box>
        );
    }

    // Not authenticated - redirect to login
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Requires admin but user is not admin: stránka se nezobrazí, ale uživatel zůstává přihlášený (spec UX IoT 5, K12)
    const obsah = requireAdmin && !isAdmin ? (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            sx={{ py: 8, px: 2 }}
            data-testid="jen-pro-spravce"
        >
            <Typography variant="h5" component="h1" gutterBottom>
                Tato část je jen pro správce jednotky.
            </Typography>
            <Button variant="contained" component={RouterLink} to="/admin" sx={{ mt: 2 }}>
                Zpět na úvod
            </Button>
        </Box>
    ) : children;

    // Feature flag gate (HO-88) má přednost před kontrolou role: vypnutý modul se tváří jako neexistující
    // stránka pro všechny, i pro člena bez role správce (review K10-K12, M6).
    if (requireFeature) {
        return <FeatureGuard flag={requireFeature}>{obsah}</FeatureGuard>;
    }

    return <>{obsah}</>;
};

export default ProtectedRoute;
